import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTrash, faPlay, faStop, faRotate, faEraser,
  faUserCheck, faForward, faCircleInfo, faUsers,
  faCircleCheck, faBan, faUndo, faUserPlus, faChair, faXmark,
  faTv, faComments, faChartPie, faUserTie, faChurch, faHandsPraying,
  faArrowTurnDown, faArrowUp, faBell
} from '@fortawesome/free-solid-svg-icons';

/*
 * Painel de detalhes/ações de um ministério dentro da assembleia.
 * Recebe as ações prontas do dashboard (que recarregam a eleição).
 */
const MinistryPanel = ({
  election, ministry, liveVotes, checkins, actions,
  isFeatured = false, featuredView = null, anotherVoting = false,
  parentMinistry = null, derivedChildren = []
}) => {
  const [candName, setCandName] = useState('');
  const [candNom, setCandNom] = useState('igreja');
  const [manualName, setManualName] = useState('');
  const [showManual, setShowManual] = useState(false);
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [confirmCancel, setConfirmCancel] = useState(false);
  // Etapa derivada (ex.: Primeiro Ancião entre os eleitos)
  const [showDerive, setShowDerive] = useState(false);
  const [deriveName, setDeriveName] = useState('Primeiro Ancião');
  const [derivePicked, setDerivePicked] = useState([]);
  const [deriving, setDeriving] = useState(false);
  const [confirmRemoveDerived, setConfirmRemoveDerived] = useState(false);

  const isVoting = ministry.status === 'voting';
  const isConfirming = ministry.status === 'confirming';
  const isDecided = ministry.status === 'decided';
  const rounds = ministry.rounds || [];
  const votesNow = (liveVotes && liveVotes.electionMinistryId === ministry._id) ? liveVotes.votesCount : 0;

  const seats = ministry.seats || 1;
  const votesPerVoter = ministry.votesPerVoter || 1;
  const multiSeat = seats > 1;
  const electedList = ministry.electedList || [];
  const seatsLeft = seats - electedList.length;
  // rodada aguardando confirmação (a última encerrada com indicados)
  const confirmingRound = isConfirming ? rounds[rounds.length - 1] : null;

  const canEditConfig = !isVoting && !isConfirming;
  const isSkipped = ministry.status === 'skipped';
  // Rodada válida mais recente (para oferecer "mostrar resultado" no telão)
  const hasValidResult = rounds.some(r => r.status === 'closed' && !r.voided);
  // O que este ministério está mostrando no telão agora
  const onTv = isVoting || isFeatured;
  const tvView = isVoting ? 'voting' : (isFeatured ? featuredView : null);
  const tvLabel = {
    voting: 'votação aberta',
    names: 'nomes em discussão',
    result: 'resultado',
    elected: 'eleito(s)',
    skipped: 'sem votação'
  }[tvView] || '';

  const handleSaveSeats = (value) => {
    const n = parseInt(value);
    if (isNaN(n) || n < 1 || n === seats) return;
    actions.updateMinistry(ministry._id, { seats: n });
  };
  const handleSaveVotesPerVoter = (value) => {
    const v = parseInt(value);
    if (isNaN(v) || v < 1 || v === votesPerVoter) return;
    actions.updateMinistry(ministry._id, { votesPerVoter: v });
  };

  const submitReject = (cid) => {
    actions.rejectNominee(ministry._id, confirmingRound.number, cid, { rejected: true, reason: rejectReason.trim() });
    setRejectingId(null);
    setRejectReason('');
  };
  const undoReject = (cid) => {
    actions.rejectNominee(ministry._id, confirmingRound.number, cid, { rejected: false });
  };

  const handleAdd = (e) => {
    e.preventDefault();
    if (!candName.trim()) return;
    actions.addCandidate(ministry._id, { name: candName.trim(), nominatedBy: candNom });
    setCandName('');
    setCandNom('igreja');
  };

  // Pode criar uma votação entre os eleitos: ministério normal, várias vagas, 2+ eleitos, sem votação em curso
  const isDerived = !!ministry.parentMinistryId;
  const canDerive = !isDerived && multiSeat && electedList.length >= 2 && !isVoting && !isConfirming;
  const childWarning = derivedChildren.length > 0
    ? `\n\nAtenção: existe a etapa "${derivedChildren.map(c => c.name).join(', ')}" criada a partir destes eleitos. Ela NÃO será apagada; revise-a depois.`
    : '';

  const openDerive = () => {
    setDeriveName(derivedChildren.length === 0 ? 'Primeiro Ancião' : '');
    setDerivePicked(electedList.map(e => e.name));
    setShowDerive(true);
  };
  const togglePick = (name) => {
    setDerivePicked(prev => (prev.includes(name) ? prev.filter(n => n !== name) : [...prev, name]));
  };
  const submitDerive = async (e) => {
    e.preventDefault();
    if (!deriveName.trim() || derivePicked.length === 0) return;
    setDeriving(true);
    const ok = await actions.derive(ministry._id, { name: deriveName.trim(), candidates: derivePicked });
    setDeriving(false);
    if (ok) setShowDerive(false);
  };

  // ---------- Discussão de nomes (marcação livre, fora de sequência) ----------
  // Ciclo ao tocar: não falado → falando agora → já falado → não falado
  const NEXT_STATE = { none: 'now', now: 'done', done: 'none' };
  const showDiscussion = canEditConfig && !isDecided && !isSkipped && ministry.candidates.length > 0;
  const electedNamesSet = new Set(electedList.map(e => e.name));
  const discCands = ministry.candidates.filter(c => !electedNamesSet.has(c.name));
  const discCount = {
    now: discCands.filter(c => c.discussion === 'now').length,
    done: discCands.filter(c => c.discussion === 'done').length
  };
  discCount.none = discCands.length - discCount.now - discCount.done;

  const cycleDiscussion = (c) => {
    actions.discussion(ministry._id, { updates: [{ cid: c._id, state: NEXT_STATE[c.discussion || 'none'] }] });
  };

  // Ao abrir a votação, avisa se ainda há nomes não discutidos (só avisa, não bloqueia)
  const handleOpenRound = () => {
    const pending = ministry.candidates
      .map((c, i) => ({ ...c, n: i + 1 }))
      .filter(c => !electedNamesSet.has(c.name) && (c.discussion || 'none') !== 'done');
    const anyMarked = discCount.now + discCount.done > 0;
    if (anyMarked && pending.length > 0) {
      const list = pending.slice(0, 8).map(c => `${c.n} · ${c.name}`).join('\n');
      const more = pending.length > 8 ? `\n… e mais ${pending.length - 8}` : '';
      if (!window.confirm(`Há ${pending.length} nome(s) ainda não marcado(s) como discutido(s):\n\n${list}${more}\n\nAbrir a votação mesmo assim?`)) return;
    }
    actions.openRound(ministry._id);
  };

  const confirmReopen = () => {
    if (!childWarning || window.confirm(`Reabrir remove os eleitos deste ministério.${childWarning}`)) {
      actions.setStatus(ministry._id, 'pending');
    }
  };

  const confirmReset = () => {
    const answer = window.prompt(`Isto apaga TODAS as rodadas e o eleito deste ministério.${childWarning}\n\nDigite ZERAR para confirmar:`);
    if (answer && answer.trim().toUpperCase() === 'ZERAR') {
      actions.resetMinistry(ministry._id);
    }
  };

  const handleManual = (e) => {
    e.preventDefault();
    if (!manualName.trim()) return;
    actions.setElected(ministry._id, manualName.trim());
    setManualName('');
    setShowManual(false);
  };

  // Guia contextual (o que fazer agora)
  let guide = null;
  if (ministry.status === 'pending' && ministry.candidates.length === 0) {
    guide = { txt: 'Adicione os candidatos indicados pela Igreja e pela Comissão para habilitar a votação.', cls: 'g-info' };
    if (!isFeatured && !anotherVoting) {
      guide.txt += ' Dica: clique em "Mostrar no telão" para a igreja acompanhar os nomes enquanto são apresentados.';
    }
  } else if (ministry.status === 'pending' && rounds.length === 0 && isFeatured) {
    guide = { txt: 'Nomes no telão. Pergunte à Comissão se indica alguém, adicione os nomes (eles aparecem na hora) e clique em "Abrir votação" quando estiver pronto.', cls: 'g-ready' };
  } else if (ministry.status === 'pending' && rounds.length === 0) {
    guide = { txt: 'Candidatos prontos. Clique em "Mostrar no telão" para a discussão de nomes ou, se já estiver tudo certo, em "Abrir votação".', cls: 'g-ready' };
  } else if (ministry.status === 'pending' && multiSeat && seatsLeft > 0 && electedList.length > 0) {
    guide = { txt: `${electedList.length} de ${seats} vaga(s) preenchida(s). Faltam ${seatsLeft}. Ajuste os candidatos e abra uma nova rodada para as vagas restantes.`, cls: 'g-warn' };
  } else if (ministry.status === 'pending' && rounds.length > 0) {
    guide = { txt: 'Resultado inconclusivo na última rodada. Ajuste os candidatos se necessário e abra uma nova rodada.', cls: 'g-warn' };
  } else if (isVoting) {
    guide = multiSeat
      ? { txt: `Votação aberta para ${seatsLeft} vaga(s), até ${votesPerVoter} nomes por votante. Encerre quando todos tiverem votado.`, cls: 'g-live' }
      : { txt: 'Votação aberta! Acompanhe os votos e clique em "Encerrar" quando todos tiverem votado.', cls: 'g-live' };
    guide.txt += ' Abriu na hora errada? Use "Cancelar votação": descarta os votos e não gera resultado.';
  } else if (isConfirming) {
    guide = { txt: 'Marque quem foi rejeitado (com observação opcional) e clique em "Confirmar eleitos". Os rejeitados reabrem vaga para nova rodada.', cls: 'g-warn' };
  } else if (isDecided) {
    const how = ministry.electedSource === 'manual' ? ' (definido manualmente / aclamação)' : '';
    guide = {
      txt: `Eleito(s): ${ministry.electedName}${how}. ${isFeatured ? 'O telão está anunciando este ministério.' : 'Clique em "Eleito(s)" na barra do telão para anunciar à igreja.'} Você ainda pode aplicar como líder no cadastro.`,
      cls: 'g-done'
    };
    if (multiSeat && !ministry.parentMinistryId && electedList.length >= 2 && derivedChildren.length === 0) {
      guide.txt += ' Se for o Ancionato, use "Votar Primeiro Ancião entre os eleitos" (abaixo) quando chegar a hora.';
    }
  } else if (isSkipped) {
    guide = { txt: 'Ministério pulado nesta assembleia. Use "Reativar" se ele voltar a ser considerado.', cls: 'g-info' };
  }

  return (
    <div className="mp">
      <div className="mp-head">
        <h2>
          {parentMinistry && <span className="mp-parent-name">{parentMinistry.name} · </span>}
          {ministry.name}
        </h2>
        {isDecided && <span className="mp-elected"><FontAwesomeIcon icon={faCircleCheck} /> {ministry.electedName}</span>}
      </div>

      {parentMinistry && (
        <div className="mp-derived-info">
          <FontAwesomeIcon icon={faArrowUp} />
          <span>
            Escolhido entre os eleitos de <strong>{parentMinistry.name}</strong>.
            Os candidatos são os nomes marcados ao criar; você ainda pode remover ou adicionar.
          </span>
          {canEditConfig && rounds.length === 0 && !isDecided && (
            !confirmRemoveDerived ? (
              <button className="btn btn-sm btn-danger-outline" onClick={() => setConfirmRemoveDerived(true)} title="Remover esta etapa (criada por engano)">
                <FontAwesomeIcon icon={faTrash} /> Remover etapa
              </button>
            ) : (
              <span className="mp-derived-remove">
                Remover “{ministry.name}”?
                <button className="btn btn-sm btn-danger" onClick={() => actions.removeDerived(ministry._id, parentMinistry._id)}>Remover</button>
                <button className="btn btn-sm btn-secondary" onClick={() => setConfirmRemoveDerived(false)}>Cancelar</button>
              </span>
            )
          )}
        </div>
      )}

      {/* Controle do telão para este ministério */}
      <div className={`mp-tv ${onTv ? 'is-on' : ''}`}>
        <span className="mp-tv-status">
          <FontAwesomeIcon icon={faTv} />
          {onTv
            ? <>No telão: <strong>{tvLabel}</strong></>
            : 'Não está no telão'}
          <span
            className="info-hint"
            data-tip="O telão mostra um ministério por vez. Durante a discussão, projete os nomes; ao abrir a votação, encerrar ou definir o eleito, o telão acompanha sozinho."
          >
            <FontAwesomeIcon icon={faCircleInfo} />
          </span>
        </span>
        {!isVoting && (
          <div className="mp-tv-actions">
            {anotherVoting ? (
              <span className="mp-tv-locked">Encerre a votação em andamento para trocar o telão</span>
            ) : (
              <>
                {!isSkipped && (
                  <button
                    className={`btn btn-sm ${onTv && tvView === 'names' ? 'btn-tv-active' : 'btn-primary'}`}
                    onClick={() => actions.feature(ministry._id, 'names')}
                    disabled={onTv && tvView === 'names'}
                    title="Projetar os nomes deste ministério para a igreja acompanhar a discussão. Nomes adicionados aparecem na hora."
                  >
                    <FontAwesomeIcon icon={faComments} /> {onTv && tvView === 'names' ? 'Nomes no telão' : 'Mostrar no telão'}
                  </button>
                )}
                {hasValidResult && (
                  <button
                    className={`btn btn-sm ${onTv && tvView === 'result' ? 'btn-tv-active' : 'btn-secondary'}`}
                    onClick={() => actions.feature(ministry._id, 'result')}
                    disabled={onTv && tvView === 'result'}
                    title="Projetar o gráfico da última rodada válida"
                  >
                    <FontAwesomeIcon icon={faChartPie} /> Resultado
                  </button>
                )}
                {electedList.length > 0 && (
                  <button
                    className={`btn btn-sm ${onTv && tvView === 'elected' ? 'btn-tv-active' : 'btn-secondary'}`}
                    onClick={() => actions.feature(ministry._id, 'elected')}
                    disabled={onTv && tvView === 'elected'}
                    title="Projetar o(s) eleito(s) deste ministério"
                  >
                    <FontAwesomeIcon icon={faHandsPraying} /> Eleito(s)
                  </button>
                )}
                {isSkipped && !onTv && (
                  <button
                    className="btn btn-sm btn-secondary"
                    onClick={() => actions.feature(ministry._id, 'names')}
                    title="Avisar no telão que este ministério não terá votação neste ano"
                  >
                    <FontAwesomeIcon icon={faForward} /> Mostrar no telão
                  </button>
                )}
                {onTv && (
                  <button
                    className="btn btn-sm btn-secondary"
                    onClick={actions.clearFeature}
                    title="Voltar o telão para a tela de espera com o QR code"
                  >
                    <FontAwesomeIcon icon={faXmark} /> Tirar do telão
                  </button>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* Configuração de vagas (cargos) e votos por votante */}
      <div className="mp-seats">
        <label className="mp-seats-field" title="Quantas pessoas serão eleitas neste ministério (ex.: ancionato = várias)">
          <FontAwesomeIcon icon={faChair} /> Vagas
          <input
            type="number"
            min="1"
            defaultValue={seats}
            disabled={!canEditConfig}
            onBlur={(e) => handleSaveSeats(e.target.value)}
          />
        </label>
        <label className="mp-seats-field" title="Quantos nomes cada votante pode marcar na cédula">
          <FontAwesomeIcon icon={faUsers} /> Votos por votante
          <input
            type="number"
            min="1"
            defaultValue={votesPerVoter}
            disabled={!canEditConfig}
            onBlur={(e) => handleSaveVotesPerVoter(e.target.value)}
          />
        </label>
        {multiSeat && (
          <span className="mp-seats-info">
            {electedList.length}/{seats} preenchidas
            {seatsLeft > 0 ? ` · ${seatsLeft} em aberto` : ' · completo'}
          </span>
        )}
      </div>

      {guide && (
        <div className={`mp-guide ${guide.cls}`}>
          <FontAwesomeIcon icon={faCircleInfo} /> {guide.txt}
        </div>
      )}

      {/* Eleitos já confirmados (várias vagas) */}
      {multiSeat && electedList.length > 0 && (
        <div className="mp-section">
          <div className="mp-section-title">Eleitos confirmados</div>
          <div className="mp-elected-list">
            {electedList.map((e, i) => (
              <span key={i} className="mp-elected-chip">
                <FontAwesomeIcon icon={faCircleCheck} /> {e.name}
                {e.votes > 0 && <em> · {e.votes} voto(s)</em>}
              </span>
            ))}
          </div>

          {/* Etapas já criadas a partir destes eleitos */}
          {derivedChildren.length > 0 && (
            <div className="mp-derived-children">
              {derivedChildren.map(c => (
                <span key={c._id} className="mp-derived-child">
                  <FontAwesomeIcon icon={faArrowTurnDown} /> {c.name}:{' '}
                  {c.status === 'decided'
                    ? <strong>{c.electedName}</strong>
                    : <em>{c.status === 'voting' ? 'votando' : 'aguardando votação'}</em>}
                </span>
              ))}
            </div>
          )}

          {/* Criar votação entre os eleitos (ex.: Primeiro Ancião) — sempre manual */}
          {canDerive && !showDerive && (
            <button className="btn btn-primary mp-derive-btn" onClick={openDerive} title="Criar uma votação só entre estes eleitos, ex.: Primeiro Ancião">
              <FontAwesomeIcon icon={faUserCheck} />{' '}
              {derivedChildren.length === 0 ? 'Votar Primeiro Ancião entre os eleitos' : 'Criar outra votação entre os eleitos'}
            </button>
          )}
          {canDerive && showDerive && (
            <form className="mp-derive" onSubmit={submitDerive}>
              <label className="mp-derive-field">
                Cargo
                <input
                  type="text"
                  value={deriveName}
                  onChange={(e) => setDeriveName(e.target.value)}
                  placeholder="Ex.: Primeiro Ancião"
                  autoFocus
                />
              </label>
              <div className="mp-derive-hint">
                Desmarque quem não é elegível ou não aceita ser candidato:
              </div>
              <div className="mp-derive-list">
                {electedList.map(e => (
                  <label key={e.name} className={`mp-derive-item ${derivePicked.includes(e.name) ? 'on' : ''}`}>
                    <input
                      type="checkbox"
                      checked={derivePicked.includes(e.name)}
                      onChange={() => togglePick(e.name)}
                    />
                    {e.name}
                  </label>
                ))}
              </div>
              <div className="mp-derive-actions">
                <button
                  type="submit"
                  className="btn btn-success"
                  disabled={deriving || !deriveName.trim() || derivePicked.length === 0}
                >
                  <FontAwesomeIcon icon={faCircleCheck} /> Criar votação ({derivePicked.length} {derivePicked.length === 1 ? 'nome' : 'nomes'})
                </button>
                <button type="button" className="btn btn-secondary" onClick={() => setShowDerive(false)} disabled={deriving}>
                  Cancelar
                </button>
              </div>
              <small className="mp-derive-note">
                A votação não abre sozinha: a etapa aparece na lista e você abre quando quiser.
              </small>
            </form>
          )}
        </div>
      )}

      {/* Painel de aceite/rejeição dos indicados (várias vagas) */}
      {isConfirming && confirmingRound && (
        <div className="mp-section mp-confirm">
          <div className="mp-section-title">
            Indicados da rodada {confirmingRound.number} — aguardando aceite
          </div>
          <div className="mp-nominees">
            {(confirmingRound.nominated || []).map((n) => {
              const cid = n.candidateId;
              const isRejecting = rejectingId === cid;
              return (
                <div key={cid} className={`mp-nominee ${n.rejected ? 'rejected' : 'accepted'}`}>
                  <div className="mp-nominee-main">
                    <span className="mp-nominee-name">
                      <FontAwesomeIcon icon={n.rejected ? faXmark : faCircleCheck} /> {n.name}
                    </span>
                    <span className="mp-nominee-votes">{n.votes} voto(s) · {n.percent}%</span>
                    {n.rejected ? (
                      <button className="btn btn-sm btn-secondary" onClick={() => undoReject(cid)} title="Desfazer rejeição">
                        <FontAwesomeIcon icon={faUndo} /> Desfazer
                      </button>
                    ) : (
                      <button className="btn btn-sm btn-danger-outline" onClick={() => { setRejectingId(cid); setRejectReason(''); }} title="Rejeitar este candidato e reabrir a vaga">
                        <FontAwesomeIcon icon={faXmark} /> Candidato rejeitado
                      </button>
                    )}
                  </div>
                  {n.rejected && n.rejectReason && (
                    <div className="mp-nominee-reason">Motivo: {n.rejectReason}</div>
                  )}
                  {isRejecting && (
                    <div className="mp-nominee-form">
                      <input
                        type="text"
                        placeholder="Observação (opcional): não aceitou, etc."
                        value={rejectReason}
                        onChange={(e) => setRejectReason(e.target.value)}
                        autoFocus
                      />
                      <button className="btn btn-sm btn-danger" onClick={() => submitReject(cid)}>Confirmar rejeição</button>
                      <button className="btn btn-sm btn-secondary" onClick={() => setRejectingId(null)}>Cancelar</button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <button className="btn btn-success mp-confirm-btn" onClick={() => actions.confirmNominees(ministry._id)} title="Confirmar: aceitos viram eleitos, rejeitados reabrem vaga">
            <FontAwesomeIcon icon={faCircleCheck} /> Confirmar eleitos
          </button>
        </div>
      )}

      {/* Contador ao vivo durante votação */}
      {isVoting && (
        <div className="mp-live">
          <div className="mp-live-count">
            <FontAwesomeIcon icon={faUsers} />
            <span><strong>{votesNow}</strong>{election.expectedVoters ? ` / ${election.expectedVoters}` : ` / ${checkins}`} votaram</span>
          </div>
          {election.expectedVoters > 0 && (
            <div className="mp-live-bar">
              <div className="mp-live-fill" style={{ width: `${Math.min(100, Math.round((votesNow / election.expectedVoters) * 100))}%` }} />
            </div>
          )}
          {election.expectedVoters > votesNow && (
            <span className="mp-live-missing">Faltam {election.expectedVoters - votesNow} voto(s)</span>
          )}
        </div>
      )}

      {/* Candidatos */}
      <div className="mp-section">
        <div className="mp-section-title">
          Candidatos
          <span className="info-hint" data-tip="Marque a origem: indicado pela Igreja ou pela Comissão de Nomeação.">
            <FontAwesomeIcon icon={faCircleInfo} />
          </span>
        </div>

        {/* Quadro da discussão: a secretaria marca livremente quem está sendo falado e quem já foi */}
        {showDiscussion && discCands.length > 0 && (
          <div className="mp-disc">
            <div className="mp-disc-head">
              <span className="mp-disc-title">
                <FontAwesomeIcon icon={faComments} /> Discussão de nomes
                <span
                  className="info-hint"
                  data-tip="Toque no nome: 1º toque = falando agora, 2º = já falado, 3º = volta. Pode marcar vários ao mesmo tempo e em qualquer ordem (ex.: dois parentes saem juntos). O telão mostra na hora."
                >
                  <FontAwesomeIcon icon={faCircleInfo} />
                </span>
              </span>
              <span className="mp-disc-count">
                <span className="d-done">{discCount.done} já falado(s)</span>
                <span className="d-now">{discCount.now} agora</span>
                <span className="d-none">{discCount.none} faltam</span>
              </span>
              <span className="mp-disc-actions">
                {discCount.now > 0 && (
                  <button
                    className="btn btn-sm btn-success"
                    onClick={() => actions.discussion(ministry._id, { action: 'finish_now' })}
                    title="Marcar todos que estão em discussão como já falados"
                  >
                    <FontAwesomeIcon icon={faCircleCheck} /> Concluir os {discCount.now} em discussão
                  </button>
                )}
                {discCount.now + discCount.done > 0 && (
                  <button
                    className="btn btn-sm btn-secondary"
                    onClick={() => { if (window.confirm('Voltar todos os nomes para "não falado"?')) actions.discussion(ministry._id, { action: 'reset' }); }}
                    title="Limpar todas as marcações"
                  >
                    <FontAwesomeIcon icon={faRotate} /> Reiniciar
                  </button>
                )}
              </span>
            </div>
            <div className="mp-disc-grid">
              {ministry.candidates.map((c, i) => {
                if (electedNamesSet.has(c.name)) return null;
                const st = c.discussion || 'none';
                return (
                  <button
                    key={c._id}
                    type="button"
                    className={`mp-disc-item d-${st}`}
                    onClick={() => cycleDiscussion(c)}
                    title={st === 'none' ? 'Tocar: falando agora' : st === 'now' ? 'Tocar: já falado' : 'Tocar: voltar para não falado'}
                  >
                    <span className="mp-cand-num">{i + 1}</span>
                    <span className="mp-disc-name">{c.name}</span>
                    {st === 'now' && <FontAwesomeIcon icon={faComments} className="mp-disc-ico" />}
                    {st === 'done' && <FontAwesomeIcon icon={faCircleCheck} className="mp-disc-ico" />}
                  </button>
                );
              })}
            </div>
            <div className="mp-disc-legend">
              <span><i className="lg lg-none" /> Não falado</span>
              <span><i className="lg lg-now" /> Falando agora</span>
              <span><i className="lg lg-done" /> Já falado</span>
            </div>
          </div>
        )}

        {ministry.candidates.length === 0 ? (
          <p className="mp-empty">Nenhum candidato ainda.</p>
        ) : (
          <div className="mp-candidates">
            {ministry.candidates.map((c, i) => (
              <div key={c._id} className="mp-candidate">
                <span className="mp-cand-num" title="Número do candidato no telão e no celular">{i + 1}</span>
                <span className="mp-cand-name">{c.name}</span>
                <span className={`mp-cand-tag ${c.nominatedBy === 'comissao' ? 'tag-com' : 'tag-igr'}`}>
                  <FontAwesomeIcon icon={c.nominatedBy === 'comissao' ? faUserTie : faChurch} /> {c.nominatedBy === 'comissao' ? 'Comissão' : 'Igreja'}
                </span>
                {canEditConfig && (
                  <button
                    className="mp-cand-del"
                    title="Remover candidato"
                    onClick={() => actions.deleteCandidate(ministry._id, c._id)}
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        {canEditConfig && (
          <form className="mp-add" onSubmit={handleAdd}>
            <input
              type="text"
              placeholder="Nome do candidato"
              value={candName}
              onChange={(e) => setCandName(e.target.value)}
            />
            <select value={candNom} onChange={(e) => setCandNom(e.target.value)} title="Quem indicou este nome">
              <option value="igreja">Igreja</option>
              <option value="comissao">Comissão</option>
            </select>
            <button type="submit" className="btn btn-sm btn-primary" title="Adicionar candidato">
              <FontAwesomeIcon icon={faUserPlus} /> Adicionar
            </button>
          </form>
        )}
      </div>

      {/* Ações da secretaria */}
      <div className="mp-actions">
        {canEditConfig && ministry.candidates.length > 0 && seatsLeft > 0 && (
          <button className="btn btn-success" onClick={handleOpenRound} title="Abrir a votação deste ministério para todos os celulares">
            <FontAwesomeIcon icon={faPlay} /> {rounds.length > 0 ? 'Nova rodada' : 'Abrir votação'}
          </button>
        )}
        {isVoting && !confirmCancel && (
          <>
            <button className="btn btn-danger" onClick={() => actions.closeRound(ministry._id)} title="Encerrar a votação e calcular o resultado">
              <FontAwesomeIcon icon={faStop} /> Encerrar votação
            </button>
            <button
              className="btn btn-secondary"
              onClick={() => actions.nudge(ministry._id)}
              disabled={checkins > 0 && votesNow >= checkins}
              title={checkins > 0 && votesNow >= checkins
                ? 'Todos os presentes já votaram'
                : `Faz os celulares de quem ainda NÃO votou apitarem e vibrarem (${votesNow} de ${checkins} presentes votaram). Quem já votou não é incomodado.`}
            >
              <FontAwesomeIcon icon={faBell} /> Chamar quem não votou
            </button>
            <button
              className="btn btn-secondary"
              onClick={() => setConfirmCancel(true)}
              title="Abriu na hora errada? Cancela sem calcular resultado: descarta os votos e volta para a discussão de nomes"
            >
              <FontAwesomeIcon icon={faBan} /> Cancelar votação
            </button>
          </>
        )}
        {isVoting && confirmCancel && (
          <div className="mp-cancel-confirm">
            <p>
              <FontAwesomeIcon icon={faBan} />{' '}
              {votesNow > 0
                ? <>Descartar <strong>{votesNow} voto(s)</strong> já recebido(s)</>
                : <>Cancelar esta votação</>}
              {' '}e voltar para a discussão de nomes? <strong>Nenhum resultado é calculado</strong> e a rodada não fica no histórico.
            </p>
            <div className="mp-cancel-actions">
              <button
                className="btn btn-danger"
                onClick={() => { setConfirmCancel(false); actions.cancelRound(ministry._id); }}
              >
                Sim, cancelar votação
              </button>
              <button className="btn btn-secondary" onClick={() => setConfirmCancel(false)}>
                Voltar
              </button>
            </div>
          </div>
        )}

        {canEditConfig && (
          <>
            <button className="btn btn-secondary" onClick={() => setShowManual((s) => !s)} title="Definir o eleito manualmente (ex.: votação no papel ou aclamação)">
              <FontAwesomeIcon icon={faUserCheck} /> Definir eleito
            </button>
            {isDecided && (
              <button className="btn btn-secondary" onClick={() => actions.applyLeader(ministry._id)} title="Gravar o eleito como líder no cadastro do ministério">
                <FontAwesomeIcon icon={faCircleCheck} /> Aplicar como líder
              </button>
            )}
            {isDecided && (
              <button className="btn btn-secondary" onClick={confirmReopen} title="Reabrir: remove o eleito e libera nova votação">
                <FontAwesomeIcon icon={faUndo} /> Reabrir
              </button>
            )}
            {ministry.status === 'pending' && (
              <button className="btn btn-secondary" onClick={() => actions.setStatus(ministry._id, 'skipped')} title="Marcar como sem votação neste ano">
                <FontAwesomeIcon icon={faForward} /> Pular
              </button>
            )}
            {ministry.status === 'skipped' && (
              <button className="btn btn-secondary" onClick={() => actions.setStatus(ministry._id, 'pending')} title="Voltar a considerar este ministério">
                <FontAwesomeIcon icon={faUndo} /> Reativar
              </button>
            )}
            {rounds.length > 0 && (
              <button className="btn btn-danger-outline" onClick={confirmReset} title="Apagar todas as rodadas e o eleito deste ministério">
                <FontAwesomeIcon icon={faEraser} /> Zerar
              </button>
            )}
          </>
        )}
      </div>

      {showManual && canEditConfig && (
        <form className="mp-manual" onSubmit={handleManual}>
          <input
            type="text"
            placeholder={multiSeat ? 'Nomes separados por vírgula' : 'Nome do eleito'}
            value={manualName}
            onChange={(e) => setManualName(e.target.value)}
            autoFocus
          />
          <button type="submit" className="btn btn-sm btn-primary">Confirmar</button>
          <button type="button" className="btn btn-sm btn-secondary" onClick={() => setShowManual(false)}>Cancelar</button>
        </form>
      )}

      {/* Histórico de rodadas */}
      {rounds.length > 0 && (
        <div className="mp-section">
          <div className="mp-section-title">Histórico de rodadas</div>
          <div className="mp-rounds">
            {rounds.map((r) => (
              <div key={r.number} className={`mp-round ${r.voided ? 'voided' : ''}`}>
                <div className="mp-round-head">
                  <strong>Rodada {r.number}</strong>
                  {r.voided && <span className="mp-round-void">Anulada</span>}
                  {!r.voided && r.status === 'closed' && (
                    (r.nominated && r.nominated.length > 0)
                      ? <span className="mp-round-ok"><FontAwesomeIcon icon={faCircleCheck} /> {r.nominated.map(n => n.name).join(', ')}</span>
                      : (r.conclusive
                          ? <span className="mp-round-ok"><FontAwesomeIcon icon={faCircleCheck} /> {r.winnerName}</span>
                          : <span className="mp-round-inc"><FontAwesomeIcon icon={faRotate} /> inconclusiva</span>)
                  )}
                  {r.status === 'open' && <span className="mp-round-live"><FontAwesomeIcon icon={faPlay} /> em andamento</span>}
                  {!r.voided && r.status === 'closed' && (
                    <button className="mp-round-voidbtn" title="Anular esta rodada" onClick={() => actions.voidRound(ministry._id, r.number)}>
                      <FontAwesomeIcon icon={faBan} /> anular
                    </button>
                  )}
                </div>
                {r.status === 'closed' && r.results && r.results.length > 0 && (
                  <div className="mp-round-results">
                    {r.results.map((res) => (
                      <span key={res.name} className={`mp-round-res ${res.elected ? 'is-elected' : ''}`}>
                        {res.name}: {res.votes} ({res.percent}%)
                      </span>
                    ))}
                    <span className="mp-round-total">
                      {r.voters ? `${r.voters} votante(s) · ${r.totalVotes} marcações` : `Total: ${r.totalVotes}`}
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default MinistryPanel;
